import { IonButtons, IonHeader, IonToolbar, IonSpinner, IonButton, IonIcon, IonPage, IonContent, IonLabel, IonItem, IonText, IonCard, IonCardHeader, IonGrid, IonRow, IonCol, IonCardContent, IonNote, IonFooter, IonInput, IonCardSubtitle, useIonAlert, IonThumbnail, IonNavLink, IonTextarea, IonTitle, IonPopover } from "@ionic/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { arrowBackOutline, closeOutline, eyeOutline, informationCircle, } from 'ionicons/icons';
import { Asset, DelegateSigningParams, PaymentDestinationInfo } from "../../types/types";
import NumberFormat from 'react-number-format';
import *  as stellarSdk from '@stellar/stellar-sdk';
import * as stellarTxBuilder from '../../services/stellar/TransactionBuilder';
import * as sep7 from '../../services/stellar/sep7';
import *  as stellarApi from '../../services/stellar/horizon/data/data';
import { shortenAccountId } from '../../services/stellar/stellar-utils';
import { useAppSelector, useAppDispatch } from '../../services/redux/hook';
import { useAccountDetailsQuery } from "../../services/api/stellar";
import { useGetQuotesQuery, useGetUserInfoQuery, useDelegateTransactionFeeQuery } from "../../services/api/ebioro/ebioro";
import { useDelegateSigningMutation } from "../../services/api/ebioro/ebioro";
import { setPaymentDestinationInfo } from "../../services/redux/slices/paymentDestinationInfo/paymentDestinationInfo";
import * as Sentry from "@sentry/react";
import './SendPage.css';


const ebioroAuthDomain = import.meta.env.VITE_API_AUTH_DOMAIN || '';
import PaymentProcessing from "../payment-processing/PaymentProcessing";
import { closeModal } from "../../services/redux/slices/opened_navigation_modal/opened_root_modal";
import { Provider } from "react-redux";
import { store } from "../../services/redux/store";
import BigNumber from "bignumber.js";

const Page: React.FC = () => {

    // Get the nav element from the BaseModalComponent
    const nav = document.querySelector("ion-nav");
    const [canGoBack, setCanGoBack] = useState(false);

    const close = () => {

        dispatch(closeModal());

    }


    const { t, i18n } = useTranslation();

    const paymentInfo: PaymentDestinationInfo = useAppSelector((state) => state.paymentDestinationInfo);
    const selectedAsset = useAppSelector((state) => state.selectedAsset);
    const asset = paymentInfo.destination_asset ? paymentInfo.destination_asset : selectedAsset
    const [accountMemoRequired, setAccountMemoRequired] = useState(false);
    const [disableButton, setDisableButton] = useState(false);
    const [delegateSigning, { isSuccess: isDelegateSuccess }] = useDelegateSigningMutation();

    const amountToSend = paymentInfo.destination_amount ? parseFloat(paymentInfo.destination_amount) : 0
    const dispatch = useAppDispatch();
    const supportedAssets = useAppSelector((state) => state.supportedAssets.supportedAssets);
    let coins = '';

    const [memo, setMemo] = useState(paymentInfo.destination_memo);
    const [message, setMessage] = useState(paymentInfo.message)

    supportedAssets.map((asset: any) => {

        if (!coins || coins === '') {
            coins = asset.code

        }
        else {
            coins = coins + ',' + asset.code;
        }

    })
    const [present] = useIonAlert();

    const presentErrorAlert = (message: any) => {

        setIsSpinner(false);
        present({
            header: 'Error',
            message: `${message}`,
            buttons: [
                {
                    text: 'Close',
                    role: 'cancel',
                    handler: () => {
                        return
                    }
                }
            ],

        })

    }
    const walletInfo = useAppSelector((state) => state.walletInfo);
    const sep10Tokens = useAppSelector((state) => state.sep10AuthToken.tokens);

    const { data: accountDetails } = useAccountDetailsQuery(walletInfo.account_key);

    const authToken = sep10Tokens[ebioroAuthDomain]

    const { data: userInfo } = useGetUserInfoQuery(sep10Tokens[ebioroAuthDomain])
    const assetBalance = accountDetails?.details.balances[asset?.identifier] ? accountDetails?.details.balances[asset?.identifier].available.toNumber() : 0
    const { data: quotes } = useGetQuotesQuery({ coins: coins, authToken: authToken });
    const [isSpinner, setIsSpinner] = useState(false);

    const [showPopover, setShowPopover] = useState(false);
    const [popoverEvent, setPopoverEvent] = useState<MouseEvent | undefined>(undefined);

    // Shared fee calculation function
    const calculateFeeAmount = (totalAmount: BigNumber, feePercentage: number, decimals: number): {
        feeAmountBN: BigNumber;
        feeAmountFormatted: string;
    } => {
        const minimumFee = new BigNumber('0.01');
        const calculatedFeeAmountBN = new BigNumber(isNaN(feePercentage) ? 0 : feePercentage).times(totalAmount);
        const feeAmountBN = BigNumber.maximum(minimumFee, calculatedFeeAmountBN);
        const feeAmountFormatted = feeAmountBN.decimalPlaces(decimals, BigNumber.ROUND_HALF_UP).toFixed(decimals);
        
        return {
            feeAmountBN,
            feeAmountFormatted
        };
    };

    // Fee configuration from API (preview + runtime)
    const { data: feeConfig, isLoading: feeLoading, isError: feeError } = useDelegateTransactionFeeQuery({});
    const feeAccountPreview: string = feeConfig?.feeAccount || '';
    const feePctPreview: number = feeConfig?.feePercentage ?? 0;
    const decimalsPreview = paymentInfo.destination_asset?.decimal_places ?? 7;
    const totalAmountBNPreview = new BigNumber(paymentInfo.destination_amount || '0');

    // Calculate fee using shared function
    const { feeAmountBN: feeAmountBNPreview } = calculateFeeAmount(totalAmountBNPreview, feePctPreview, decimalsPreview);

    const feeAmountPreview = feeAmountBNPreview.decimalPlaces(decimalsPreview, BigNumber.ROUND_HALF_UP);
    const recipientAmountPreview = totalAmountBNPreview.decimalPlaces(decimalsPreview, BigNumber.ROUND_HALF_UP);
    const totalToPayPreview = totalAmountBNPreview.plus(feeAmountBNPreview).decimalPlaces(decimalsPreview, BigNumber.ROUND_HALF_UP);
    const requiredTotalBN = totalAmountBNPreview.plus(feeAmountBNPreview);
    const hasEnoughFunds = new BigNumber(assetBalance).gte(requiredTotalBN);

    useEffect(() => {
    }, [assetBalance, amountToSend, disableButton]);

    // Ensure `disableButton` updates correctly
    useEffect(() => {
        const updateButtonState = () => {
            setDisableButton(feeLoading || feeError || !hasEnoughFunds);
        };

        const delay = setTimeout(updateButtonState, 500);
        return () => clearTimeout(delay);
    }, [assetBalance, amountToSend, feePctPreview, paymentInfo.destination_asset, feeLoading, feeError, hasEnoughFunds]);

    // Ensure correct memo handling

    useEffect(() => {
        const init = async () => {
            try {
                const account = await stellarApi.horizon.loadAccount(paymentInfo.destination_address as string);

                if (account.data_attr["config.memo_required"] === "MQ==" && !paymentInfo.destination_memo) {
                    setDisableButton(true);
                }

                if (!hasEnoughFunds) {
                    setDisableButton(true);
                }

                const goBack = await nav?.canGoBack();
                setCanGoBack(goBack ?? false);
            } catch (error) {
                Sentry.captureException(error);
                present({ header: "Error", message: "Failed to load account data.", buttons: ["Close"] });
            }
        };

        init();
    }, [canGoBack, paymentInfo, assetBalance, amountToSend]);





    const getAssetTotalValue = (asset: Asset) => {

        for (const [key, value] of Object.entries(quotes)) {

            if (key === asset.code && userInfo) {

                const values: any = value;
                const quoteValue = values[userInfo.currency_code];
                const quote = (quoteValue * amountToSend);
                return quote
            }

        }
        return 0

    }

    const sendPayment = async () => {

        setDisableButton(true);
        setIsSpinner(true);

        try {

            let trustedAsset = undefined;

            const destinationAsset = paymentInfo.destination_asset?.code;
            const destinationIssuer = paymentInfo.destination_asset?.identifier.split(":")[1];


            // first check if destination trusts the asset:

            if (destinationAsset && destinationIssuer) {

                const [error, canReceive] = await stellarTxBuilder.checkAssetTrust(paymentInfo.destination_address as string, destinationAsset, destinationIssuer);

                if (error) {

                    const err = t('send.page.CANNOT-MAKE-PAYMENT-DESTINATION')
                    presentErrorAlert(err);
                    setIsSpinner(false);
                    return;
                }

                trustedAsset = canReceive
            }

            if (walletInfo && paymentInfo.destination_amount) {

                let stellarTx: stellarSdk.Transaction<stellarSdk.Memo<stellarSdk.MemoType>, stellarSdk.Operation[]> | null;

                // Calculate fee amount once at the beginning
                const decimals = paymentInfo.destination_asset?.decimal_places ?? 7;
                const totalAmountBN = new BigNumber(paymentInfo.destination_amount);

                // Load fee configuration
                if (feeLoading || feeError || !feeConfig) {
                    presentErrorAlert('Unable to load fee configuration. Please try again.');
                    setIsSpinner(false);
                    return;
                }
                const feePct: number = feeConfig.feePercentage ?? 0;

                // Calculate fee using shared function
                const { feeAmountFormatted: feeAmount } = calculateFeeAmount(totalAmountBN, feePct, decimals);

                // if asset not trusted in destination show error.
                if (trustedAsset === false) {

                    presentErrorAlert(t('send.page.NO-TRUST'));
                    setIsSpinner(false);
                    return

                }

                else {

                    // Build single-transaction with two payment operations (recipient + fee)
                    const feeAccount: string = feeConfig.feeAccount || '';

                    // Prepare asset
                    let assetObj = stellarSdk.Asset.native();
                    if (destinationAsset && destinationIssuer) {
                        assetObj = new stellarSdk.Asset(destinationAsset, destinationIssuer);
                    }

                    const recipientAmount = totalAmountBN.decimalPlaces(decimals, BigNumber.ROUND_HALF_UP).toFixed(decimals);

                    const ops: stellarSdk.xdr.Operation<stellarSdk.Operation>[] = [];

                    // Always add recipient payment
                    ops.push(
                        stellarSdk.Operation.payment({
                            destination: paymentInfo.destination_address as string,
                            asset: assetObj,
                            amount: recipientAmount,
                        })
                    );

                    // Add fee payment only when configured and > 0
                    if (feeAccount && new BigNumber(feeAmount).isGreaterThan(0)) {
                        ops.push(
                            stellarSdk.Operation.payment({
                                destination: feeAccount,
                                asset: assetObj,
                                amount: feeAmount,
                            })
                        );
                    }

                    const [error, transaction] = await stellarTxBuilder.builStellarTx(
                        ops,
                        walletInfo.account_key as string,
                        walletInfo.device_private_key as string,
                        paymentInfo.destination_memo,
                        paymentInfo.destination_memo_type
                    );

                    if (error) {
                        Sentry.captureException(error);
                        presentErrorAlert(JSON.stringify(error));
                        setIsSpinner(false);
                        return;
                    }

                    stellarTx = transaction;

                }

                if (stellarTx) {

                    const uri = createSep7URI(stellarTx);

                    if (!uri) {

                        presentErrorAlert('Not valid uri creation');
                        setIsSpinner(false);
                        return

                    }

                    else {

                        let params: DelegateSigningParams = {
                            uri: uri,
                            account: walletInfo.account_key as string,
                            asset_code: destinationAsset,
                            asset_issuer: destinationIssuer ? destinationIssuer : "",
                            name: paymentInfo.destination_username?.name as string,
                            msg: paymentInfo.message as string,
                            fees: feeAmount
                        }

                        try {


                            const response = await delegateSigning(params).unwrap();
                            const stellarTxId = response.stellar_transaction_id as string;
                            setIsSpinner(false);
                            nav?.push(() => <PaymentProcessing transactionId={stellarTxId} />)


                        } catch (error: any) {

                            Sentry.captureException(error);
                            setIsSpinner(false);


                            if (error.message === 'submission_error') {
                                presentErrorAlert(t('transaction.submission.ERROR'))

                            }

                            else {

                                presentErrorAlert(t('application.uncaught.ERROR'));
                            }

                            return

                        }

                    }

                }


            }

        } catch (error) {
            setIsSpinner(false);
            presentErrorAlert(t('application.uncaught.ERROR'));
            return

        }

    }

    const createSep7URI = (stellarTx: stellarSdk.Transaction<stellarSdk.Memo<stellarSdk.MemoType>, stellarSdk.Operation[]>) => {

        if (walletInfo) {

            return sep7.createSep7URI(stellarTx, walletInfo.account_key as string);

        }

        return null

    }

    const addMemo = (value: any) => {

        setMemo(value);

        if (accountMemoRequired && !value) {
            setDisableButton(true);

        }

        else {

            let updatedPaymentInfo = { ...paymentInfo }
            updatedPaymentInfo.destination_memo = value;
            updatedPaymentInfo.destination_memo_type = 'text';
            dispatch(setPaymentDestinationInfo(updatedPaymentInfo));
            setDisableButton(false);

        }

    }

    const addMessage = (value: any) => {

        let updatedPaymentInfo = { ...paymentInfo }
        updatedPaymentInfo.message = value;
        dispatch(setPaymentDestinationInfo(updatedPaymentInfo))
        setMessage(value);

    }

    const renderHeader = () => {
        if (canGoBack) {
            return (
                <IonHeader className="ion-no-border">
                    <IonToolbar style={{ marginTop: "0%" }}>
                        <IonNavLink routerDirection={'back'}>
                            <IonButtons slot='start'>
                                <IonButton color='primary'>
                                    <IonIcon slot='start' icon={arrowBackOutline} />
                                </IonButton>
                            </IonButtons>
                        </IonNavLink>
                        <IonTitle> {t('send.page.HEADER') as string} </IonTitle>
                    </IonToolbar>
                </IonHeader>
            );
        } else {
            return (
                <IonHeader className="ion-no-border">
                    <IonToolbar style={{ marginTop: "0%" }}>
                        <IonButtons slot='start' onClick={() => { close() }} >
                            <IonButton color='primary'>
                                <IonIcon slot='start' icon={closeOutline} />
                            </IonButton>
                        </IonButtons>
                        <IonTitle> {t('send.page.HEADER') as string} </IonTitle>
                    </IonToolbar>
                </IonHeader>
            );
        }
    };

    const handleRowClick = (e: MouseEvent) => {
        setPopoverEvent(e);
        setShowPopover(true);
    };

    return (
        <IonPage>


            {renderHeader()}

            <IonContent >

                {/** AMOUNT INFO */}
                <IonCard>

                    <IonCardContent>
                        <IonItem lines='none'>
                            <IonLabel slot="start" >
                                <h2 className="ion-text-wrap">

                                    {paymentInfo.destination_amount} {paymentInfo.destination_asset?.display_code}
                                </h2>
                                <IonNote className='accountsPage_IonNote' color={'primary'}>
                                    <NumberFormat value={
                                        assetBalance ? getAssetTotalValue(asset) : 0}
                                        displayType={'text'} thousandSeparator={true} suffix={" " + userInfo?.currency_code} decimalSeparator="." decimalScale={2} fixedDecimalScale={true} />
                                </IonNote>
                            </IonLabel>
                            <IonThumbnail className="accountsPage_Thumbnail_Flag" slot="end">
                                <img src={asset.icon} />
                            </IonThumbnail>
                        </IonItem>

                    </IonCardContent>
                </IonCard>

                {/** DETAILS INFO */}

                <IonCard>
                    <IonCardHeader>
                        <IonCardSubtitle className="ion-text-start">{t('send.page.DETAILS')}</IonCardSubtitle>
                    </IonCardHeader>

                    <IonCardContent>
                        <IonGrid className="ion-padding">
                            <IonRow onClick={(e) => handleRowClick(e as any)}>
                                <IonCol size='3'>
                                    <IonItem className="ion-no-padding" lines="none">
                                        <h5>
                                            {t('send.page.TO')}
                                        </h5>
                                    </IonItem>
                                </IonCol>
                                <IonCol>
                                    {
                                        paymentInfo.destination_username && (
                                            <IonItem className="ion-no-padding" lines="none">
                                                <IonLabel>
                                                    <h5>
                                                        {paymentInfo.destination_username.name}
                                                    </h5>
                                                    <IonNote color={'primary'}>
                                                        {paymentInfo.destination_username.username}
                                                    </IonNote>
                                                </IonLabel>
                                                <IonIcon icon={eyeOutline} slot="end" color="primary" />
                                            </IonItem>
                                        )
                                    }

                                    {
                                        !paymentInfo.destination_username && (
                                            <IonItem className="ion-no-padding" lines="none">
                                                <IonLabel>
                                                    {shortenAccountId(paymentInfo.destination_address ? paymentInfo.destination_address : '')}
                                                </IonLabel>
                                                <IonIcon icon={eyeOutline} slot="end" color="primary" />
                                            </IonItem>
                                        )
                                    }

                                </IonCol>
                            </IonRow>
                            <IonRow hidden={paymentInfo.destination_username?.name ? false : true}>
                                <IonCol size="5">
                                    <IonItem className="ion-no-padding" lines="none">
                                        <h5>
                                            {t('send.page.Message')}
                                        </h5>
                                    </IonItem>

                                </IonCol>
                                <IonCol size="7">

                                    <IonItem className="ion-no-padding" lines="none">
                                        <IonTextarea color='primary'
                                            className="ion-text-wrap"
                                            value={message}
                                            onIonInput={(e: any) => addMessage(e.detail.value!)}

                                            placeholder={t('send.page.OPTIONAL_MESSAGE') as string}>
                                        </IonTextarea>
                                    </IonItem>
                                </IonCol>
                            </IonRow>

                            <IonRow hidden={paymentInfo.destination_username?.name ? true : false}>
                                <IonCol size='3' className='ion-text-left'>
                                    <IonItem className="ion-no-padding" lines="none">
                                        <h5>
                                            {t('send.page.Memo')}
                                        </h5>

                                    </IonItem>

                                </IonCol>
                                <IonCol>
                                    <IonInput
                                        label="Memo"
                                        labelPlacement="floating"
                                        fill="outline"
                                        mode='md'
                                        value={memo}
                                        onIonInput={(e: any) => addMemo(e.detail.value!)}
                                        placeholder={accountMemoRequired ? t('send.page.Memo-REQUIRED_MESSAGE') as string : t('send.page.Memo-OPTIONAL_MESSAGE') as string}>

                                    </IonInput>


                                </IonCol>
                            </IonRow>

                            <IonRow style={{ marginTop: '10%' }} hidden={paymentInfo.destination_username?.name ? true : false}>
                                <IonCol>

                                    <IonItem lines='none'>
                                        <IonIcon icon={informationCircle} color='primary' slot='start'></IonIcon>

                                        <p className='ion-text-justify' >

                                            {t('send.page.MEMO-WARNING')}
                                        </p>

                                    </IonItem>
                                </IonCol>
                            </IonRow>



                        </IonGrid>

                    </IonCardContent>
                </IonCard>

                {feeAccountPreview && totalAmountBNPreview.isGreaterThan(0) && feePctPreview > 0 && (
                    <IonCard>
                        <IonCardHeader>
                            <IonCardSubtitle className="ion-text-start">{t('send.page.PAYMENT_BREAKDOWN')}</IonCardSubtitle>
                        </IonCardHeader>
                        <IonCardContent>
                            <IonGrid className="ion-padding">

                                {/* Recipient Receives Row */}
                                <IonRow>
                                    <IonCol size="6">
                                        <IonItem className="ion-no-padding" lines="none">
                                            <h5>
                                                {paymentInfo.destination_username?.username
                                                    ? `${paymentInfo.destination_username.username} ${t('send.page.RECEIVES')}`
                                                    : paymentInfo.destination_username?.name
                                                        ? `${paymentInfo.destination_username.name} ${t('send.page.RECEIVES')}`
                                                        : t('send.page.RECIPIENT_RECEIVES')
                                                }
                                            </h5>
                                        </IonItem>
                                    </IonCol>
                                    <IonCol className="ion-text-right">
                                        <IonText>
                                            {recipientAmountPreview.toFixed(decimalsPreview)} {paymentInfo.destination_asset?.display_code}
                                        </IonText>
                                    </IonCol>
                                </IonRow>

                                {/* Fee Row */}
                                <IonRow>
                                    <IonCol size="6">
                                        <IonItem className="ion-no-padding" lines="none">
                                            <h5>
                                                {t('send.page.FEE')}
                                            </h5>
                                        </IonItem>
                                    </IonCol>
                                    <IonCol className="ion-text-right">
                                        <IonText>
                                            {feeAmountPreview.toFixed(2)} {paymentInfo.destination_asset?.display_code}
                                        </IonText>
                                    </IonCol>
                                </IonRow>


                                {/* You Pay Row */}
                                <IonRow>
                                    <IonCol size="6">
                                        <IonItem className="ion-no-padding" lines="none">
                                            <h5>
                                                {t('send.page.YOU_PAY')}
                                            </h5>
                                        </IonItem>
                                    </IonCol>
                                    <IonCol className="ion-text-right">
                                        <IonText>
                                            <strong>{totalToPayPreview.toFixed(decimalsPreview)} {paymentInfo.destination_asset?.display_code}</strong>
                                        </IonText>
                                    </IonCol>
                                </IonRow>

                            </IonGrid>
                        </IonCardContent>
                    </IonCard>
                )}

                {/* <IonCard>
                    <IonCardHeader>
                        <IonCardSubtitle className="ion-text-start">Details</IonCardSubtitle>
                    </IonCardHeader>
                    <IonCardContent>
                        <IonGrid className="ion-padding">

                        </IonGrid>
                        

                    </IonCardContent>
                </IonCard> */}


            </IonContent>

            <IonPopover
                isOpen={showPopover}
                event={popoverEvent}
                onDidDismiss={() => setShowPopover(false)}
            >
                <IonItem>
                    <IonLabel>
                        <h5>{t('send.page.ADDRESS')}</h5>
                        <p>{paymentInfo.destination_address}</p>
                    </IonLabel>
                </IonItem>
                <IonItem>
                    <IonLabel>
                        <h5>{t('send.page.Memo')}</h5>
                        <p>{paymentInfo.destination_memo || t('send.page.NO-MEMO')}</p>
                    </IonLabel>
                </IonItem>
            </IonPopover>

            <IonFooter className="ion-no-border">
                <IonToolbar>
                    <IonButton
                        type="submit"
                        color='primary'
                        disabled={disableButton}
                        onClick={() => { sendPayment() }}
                        expand="block"
                        id="pageFooterButton">
                        {isSpinner ? (<IonSpinner className="BUTTON-SPINNER"></IonSpinner>)
                            :
                            !hasEnoughFunds ?
                                t('send.page.NOT-ENOUGH-FUNDS') : t('send.page.BTN.LABEL')
                        }
                    </IonButton>
                </IonToolbar>
            </IonFooter>

        </IonPage>
    )
}


// export default React.memo(SendPage)

const SendPage: React.FC = () => (
    <Provider store={store}>
        <Page />
    </Provider>
);

export default SendPage;